<template>
    <!-- Hero -->
    <section>
        <OrganismsAboutHero :title="pageContent?.title" />
    </section>
    <section class="relative">
        <!-- Milestones -->
        <OrganismsMilestones :milestones="pageContent?.milestones" />
        <!-- Landscapes -->
        <OrganismsLandscapes :landscapes="landscapesContent" />
        <!-- Curved Slogan -->
        <OrganismsAboutSlogan />
        <!-- Planets -->
        <OrganismsPlanets />
    </section>

    <!-- Awards -->
    <section class="relative z-[var(--z-awards)] bg-base-bgGrey">
        <OrganismsAwards
            v-if="pageContent?.awards?.data?.length > 0"
            :awards="pageContent?.awards"
        />
    </section>
    <!-- Teams -->
    <section
        class="relative z-[var(--z-teams)] bg-base-bgGrey"
        v-if="
            shouldRender(teamsContent.data) &&
            teamsContent.data?.some((item) => item.isRender)
        "
    >
        <OrganismsTeams :teams="teamsContent" />
    </section>
</template>

<script setup>
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { shouldRender } from "~/lib/utils";
import gamaGlobeUrl from "@/assets/3D/gamaGlobe.glb";
import gamaGlobeTextureUrl from "@/assets/3D/gamaGlobe-texture-4K.jpg";
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(ScrollToPlugin);

// Preload Three.js 資產 —— 讓網路下載跟 JS chunk 解析、Three.js 初始化並行，
// LandscapeExperience onMounted 觸發 GLTFLoader.load() 時命中 preload cache。
useHead({
    link: [
        { rel: "preload", as: "fetch", href: gamaGlobeUrl, crossorigin: "anonymous" },
        { rel: "preload", as: "image", href: gamaGlobeTextureUrl },
        { rel: "preload", as: "fetch", href: "/about/glb/blue_ring.glb", crossorigin: "anonymous" },
        { rel: "preload", as: "fetch", href: "/about/glb/earth.glb", crossorigin: "anonymous" },
        { rel: "preload", as: "fetch", href: "/about/glb/green_pic.glb", crossorigin: "anonymous" },
        { rel: "preload", as: "fetch", href: "/about/glb/green_ring.glb", crossorigin: "anonymous" },
        { rel: "preload", as: "fetch", href: "/about/glb/red_pic.glb", crossorigin: "anonymous" },
        { rel: "preload", as: "fetch", href: "/about/glb/yellow_pic.glb", crossorigin: "anonymous" },
        { rel: "preload", as: "image", href: "/about/star.png" },
    ],
});

const { fetchPage, fetchAllPosts, fetchMedias } = useFetchAPI();
const { setMeta } = useMeta();
const { data } = await fetchPage("about");
const pageContent = data.value?.meta_box ?? {};

const landscapesContent = reactive({
    title: pageContent?.landscapes?.title || [],
    subtitle: pageContent?.landscapes?.subtitle || [],
    data: pageContent?.landscapes?.data || [],
    minorText: pageContent?.landscapes?.minorText || "",
});
const teamsContent = reactive({
    title: pageContent?.teams?.title || [],
    data: pageContent?.teams?.data || [],
});

// Fetch brands
if (pageContent.landscapes?.data && pageContent.landscapes.data.length > 0) {
    try {
        const { data } = await fetchAllPosts("brand");
        // Create a map of brand ID -> brand object
        const brandMap = new Map(data.value.map((brand) => [brand.id, brand]));
        // Replace brand IDs with brand objects
        landscapesContent.data = pageContent.landscapes.data.map((category) => {
            if (category.brands) {
                return {
                    ...category,
                    brands: category.brands
                        .map((id) => brandMap.get(parseInt(id)))
                        .filter(Boolean),
                };
            }
            return category;
        });
    } catch (error) {
        // console.error("Error fetching awards:", error);
    }
}

// Fetch Teams Images
if (pageContent.teams?.data && pageContent.teams.data.length > 0) {
    try {
        // Step 1: Collect all unique image IDs from team members
        const allMemberImageIds = new Set();
        pageContent.teams.data.forEach((team) => {
            if (team.members && Array.isArray(team.members)) {
                team.members.forEach((member) => {
                    if (
                        member.image &&
                        Array.isArray(member.image) &&
                        member.image.length > 0 &&
                        member.image[0]
                    ) {
                        allMemberImageIds.add(String(member.image[0]));
                    }
                });
            }
        });

        const uniqueImageIds = Array.from(allMemberImageIds);

        let imageMap = new Map();

        if (uniqueImageIds.length > 0) {
            // Step 2: Fetch all image data
            // Assuming fetchMedias returns { data: Ref<Array<ImageObject>>, error: Ref }
            const { data: imageDataArrayRef, error: imageError } =
                await fetchMedias(uniqueImageIds);

            if (imageError.value) {
                // console.error(
                //     "Failed to fetch member images:",
                //     imageError.value,
                // );
                // Decide if you want to throw or proceed with empty imageMap
            }

            // Step 3: Create image lookup map
            // Access .value if imageDataArrayRef is a Vue ref
            const fetchedImages = imageDataArrayRef?.value || [];
            fetchedImages.forEach((imgData) => {
                if (imgData && imgData.id !== undefined) {
                    imageMap.set(String(imgData.id), imgData); // Store the whole image object
                }
            });
        }

        // Step 4: Replace/augment member image ID with the fetched image object/data
        const updatedTeamsData = pageContent.teams.data.map((team) => {
            // Shallow copy the team to avoid mutating original pageContent deeply
            const newTeam = { ...team };

            if (team.members && Array.isArray(team.members)) {
                newTeam.members = team.members.map((member) => {
                    // Shallow copy the member
                    const newMember = { ...member };
                    if (
                        member.image &&
                        Array.isArray(member.image) &&
                        member.image.length > 0 &&
                        member.image[0]
                    ) {
                        const imageId = String(member.image[0]);
                        const fetchedImageObject = imageMap.get(imageId);
                        if (fetchedImageObject) {
                            // Replace the original image ID array with the fetched image object
                            // Or add a new property like 'imageData' or 'imageUrl'
                            newMember.image = fetchedImageObject; // Example: adding a new key
                            // Alternatively, if you want to replace member.image:
                            // newMember.image = fetchedImageObject;
                        } else {
                            // If image not found, keep original or set to null/placeholder
                            newMember.image = null; // Or keep original member.image
                        }
                    }
                    return newMember;
                });
            }
            return newTeam;
        });

        teamsContent.data = updatedTeamsData;
    } catch (err) {
        // console.error("Error processing team member images:", err);
        // Fallback: populate with original data if processing fails (optional)
        // teamsContent.data = pageContent.teams.data.map(team => ({...team}));
    }
}

await setMeta(data.value);
</script>
